import { useState } from 'react';
import type { ReactNode } from 'react';
import { NAV_GROUPS, sectionById } from './navConfig';
import type { SectionId } from './navConfig';

type Props = {
  active: SectionId;
  onNavigate: (id: SectionId) => void;
  children: ReactNode;
};

export default function AdminShell({ active, onNavigate, children }: Props) {
  const [collapsed, setCollapsed] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const section = sectionById(active);

  const go = (id: SectionId) => {
    onNavigate(id);
    setDrawerOpen(false);
  };

  const navList = (compact: boolean) => (
    <nav className="px-2.5 pb-6">
      {NAV_GROUPS.map((group) => (
        <div key={group.id} className="mt-5 first:mt-0">
          {!compact && (
            <p className="px-2 mb-2 font-label uppercase tracking-[0.2em] text-[10px] font-bold text-foreground-400">{group.label}</p>
          )}
          {compact && <div className="mx-2 mb-2 h-px bg-background-200" />}
          <ul className="space-y-1">
            {group.items.map((item) => {
              const on = active === item.id;
              return (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => go(item.id)}
                    title={compact ? item.label : undefined}
                    aria-current={on ? 'page' : undefined}
                    className={`group flex w-full items-center gap-3 rounded-md font-label font-semibold uppercase tracking-wide text-[11px] whitespace-nowrap cursor-pointer transition-colors ${
                      compact ? 'justify-center px-0 py-2.5' : 'px-3 py-2.5'
                    } ${on ? 'bg-foreground-950 text-background-50' : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-950'}`}
                  >
                    <span className={`w-5 h-5 flex items-center justify-center text-base shrink-0 ${on ? 'text-accent-400' : 'text-foreground-500 group-hover:text-primary-600'}`}>
                      <i className={item.icon}></i>
                    </span>
                    {!compact && <span className="truncate">{item.label}</span>}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </nav>
  );

  return (
    <div className="pt-16 md:pt-20">
      <div className="flex">
        {/* Desktop sidebar */}
        <aside
          className={`hidden lg:flex lg:flex-col lg:sticky lg:top-20 lg:h-[calc(100vh-5rem)] bg-background-50 border-r border-background-200 overflow-y-auto shrink-0 transition-all duration-200 ${collapsed ? 'w-[68px]' : 'w-64'}`}
        >
          <div className="flex items-center justify-between gap-2 px-3 py-4 border-b border-background-200">
            {!collapsed && (
              <div className="min-w-0">
                <p className="font-label uppercase tracking-[0.2em] text-[10px] font-bold text-primary-600">Command center</p>
                <p className="font-heading font-bold text-foreground-950 truncate">Management</p>
              </div>
            )}
            <button
              type="button"
              onClick={() => setCollapsed((c) => !c)}
              aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
              className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-600 hover:text-foreground-950 hover:bg-background-100 cursor-pointer shrink-0"
            >
              <i className={collapsed ? 'ri-arrow-right-s-line' : 'ri-arrow-left-s-line'}></i>
            </button>
          </div>
          {navList(collapsed)}
        </aside>

        {/* Content column */}
        <div className="flex-1 min-w-0">
          {/* Admin top bar */}
          <div className="sticky top-16 md:top-20 z-30 bg-background-50/95 backdrop-blur border-b border-background-200 px-4 md:px-6 py-2.5 flex items-center gap-3">
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              aria-label="Open navigation"
              className="lg:hidden w-10 h-10 flex items-center justify-center rounded-md text-foreground-950 hover:bg-background-100 cursor-pointer shrink-0"
            >
              <i className="ri-menu-2-line text-xl"></i>
            </button>
            <div className="min-w-0 flex-1">
              <p className="font-label uppercase tracking-widest text-[10px] text-foreground-500 hidden sm:block">Management demo</p>
              <h1 className="font-heading font-extrabold uppercase text-lg md:text-xl text-foreground-950 truncate leading-tight">{section.label}</h1>
            </div>
            <span className="hidden md:inline-flex items-center gap-1.5 bg-secondary-100 text-secondary-900 border border-secondary-200 font-label font-bold uppercase tracking-widest text-[10px] px-2.5 py-1 rounded-sm whitespace-nowrap">
              <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-flask-line"></i></span>
              Sample data
            </span>
          </div>

          {/* Persistent demo banner */}
          <div className="bg-accent-100 border-b border-accent-300 px-4 md:px-6 py-2 flex items-start gap-2.5">
            <span className="w-4 h-4 flex items-center justify-center text-accent-800 shrink-0 mt-0.5"><i className="ri-alert-line"></i></span>
            <p className="text-xs md:text-sm text-accent-900">
              <strong className="font-semibold">Management Demo — Sample Data. Changes Are Not Saved.</strong> Nothing here reaches the live site.
            </p>
          </div>

          <main className="px-4 md:px-6 lg:px-8 py-5 md:py-7">
            <p className="text-sm text-foreground-600 mb-5 max-w-3xl">{section.description}</p>
            {children}
          </main>
        </div>
      </div>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <button type="button" aria-label="Close navigation" onClick={() => setDrawerOpen(false)} className="absolute inset-0 bg-foreground-950/50 cursor-pointer"></button>
          <div className="absolute top-0 left-0 h-full w-72 max-w-[85vw] bg-background-50 border-r border-background-200 overflow-y-auto shadow-none">
            <div className="flex items-center justify-between gap-2 px-4 py-4 border-b border-background-200">
              <div>
                <p className="font-label uppercase tracking-[0.2em] text-[10px] font-bold text-primary-600">Command center</p>
                <p className="font-heading font-bold text-foreground-950">Management</p>
              </div>
              <button type="button" onClick={() => setDrawerOpen(false)} aria-label="Close navigation" className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-700 hover:bg-background-100 cursor-pointer">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            {navList(false)}
          </div>
        </div>
      )}
    </div>
  );
}